import { computed, ref } from 'vue';

import { ElLoading } from 'element-plus';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import {
    ARRAY_MAX_LENGTH,
    HttpStatus,
    INPUT_TEXT_MAX_LENGTH,
    TEXTAREA_MAX_LENGTH,
} from '@/common/constants';
import {
    showErrorNotificationFunction,
    showSuccessNotificationFunction,
} from '@/common/helpers';
import { projectModule } from '@/features/project/store';
import yup from '@/plugins/yup';

import { projectPlanningService } from '../services/planning.service';
import { projectPlanningModule } from '../store';

const resourceGroupSchema = yup.object({
    resourceGroupId: yup.string().max(INPUT_TEXT_MAX_LENGTH).required().trim(),
    name: yup.string().max(INPUT_TEXT_MAX_LENGTH).required().trim(),
    resourceIds: yup
        .array()
        .of(yup.string().optional().optional().nullable())
        .max(ARRAY_MAX_LENGTH)
        .optional(),
    description: yup.string().max(TEXTAREA_MAX_LENGTH).optional().trim(),
});

export const innitResourceGroup = {
    resourceGroupId: '',
    name: '',
    resourceIds: [],
    description: '',
};

export const setupResourceGroupForm = () => {
    const { t } = useI18n();
    const isCreate = computed(() => !projectPlanningModule.selectedResourceGroup?._id);

    const { handleSubmit, errors, resetForm, validate, setErrors } = useForm({
        initialValues: innitResourceGroup,
        validationSchema: resourceGroupSchema,
    });

    const additionalFields = ref<Record<string, any>>({});

    const onSubmit = handleSubmit(async (values) => {
        let response;
        const loading = ElLoading.service({});

        if (!isCreate.value) {
            response = await projectPlanningService.updateResourceGroup(
                projectPlanningModule.selectedResourceGroup?._id || '',
                {
                    resourceGroupId: values.resourceGroupId || '',
                    name: values.name || '',
                    resourceIds: values.resourceIds || [],
                    planningId: projectPlanningModule.planning?._id || '',
                    description: values.description || '',
                    projectId: projectModule.selectedProjectId || '',
                    additionalFields: additionalFields.value,
                },
            );
        } else {
            response = await projectPlanningService.createResourceGroup({
                resourceGroupId: values.resourceGroupId || '',
                name: values.name || '',
                resourceIds: values.resourceIds || [],
                planningId: projectPlanningModule.planning?._id || '',
                description: values.description || '',
                projectId: projectModule.selectedProjectId || '',
                additionalFields: additionalFields.value,
            });
        }
        loading.close();

        if (response.success) {
            showSuccessNotificationFunction(
                !isCreate.value
                    ? t('planning.resourceGroup.message.updateSuccess')
                    : t('planning.resourceGroup.message.createSuccess'),
            );
            projectPlanningModule.setIsShowResourceGroupDetailForm(false);
            const loading = ElLoading.service({
                target: '.main-wrapper',
            });
            await projectPlanningModule.getResourceGroupList({
                planningId: projectPlanningModule.planning?._id || '',
                projectId: projectModule.selectedProjectId || '',
            });
            loading.close();
            projectPlanningModule.setSelectedResourceGroup(null);
        } else {
            if (response.code === HttpStatus.ITEM_NOT_FOUND) {
                projectPlanningModule.setIsShowResourceGroupDetailForm(false);
                const loading = ElLoading.service({
                    target: '.main-wrapper',
                });
                await projectPlanningModule.getResourceGroupList({
                    planningId: projectPlanningModule.planning?._id || '',
                    projectId: projectModule.selectedProjectId || '',
                });
                loading.close();
                projectPlanningModule.setSelectedResourceGroup(null);
            }
            showErrorNotificationFunction(response.message);
        }
    });

    const { value: name } = useField('name');
    const { value: resourceGroupId } = useField('resourceGroupId');
    const { value: resourceIds } = useField('resourceIds');
    const { value: description } = useField('description');

    return {
        errors,
        onSubmit,
        resetForm,
        validate,
        setErrors,
        name,
        resourceIds,
        description,
        additionalFields,
        resourceGroupId,
    };
};
